import React, { useState, useEffect, useCallback, useRef } from "react";
import type { SortingState } from "@tanstack/react-table";
import type { V2Schedule, ScheduleTableRow } from "../../../models";
import { fetchV2Schedules, fetchScheduleTable } from "../../../api/clientV2";
import WBSSidebar from "../WBSSidebar/WBSSidebar";
import type { PathEntry } from "../WBSSidebar/WBSSidebar";
import ScheduleView from "../ScheduleView/ScheduleView";
import { buildScopeKey } from "./scopeKey";
import "./SchedulePage.css";

const PAGE_LIMIT = 100;

function sortParams(sorting: SortingState) {
  if (sorting.length === 0) return { sortBy: undefined, sortDir: undefined };
  return {
    sortBy: sorting[0].id,
    sortDir: (sorting[0].desc ? "desc" : "asc") as "asc" | "desc",
  };
}

const SchedulePage: React.FC = () => {
  const [schedules, setSchedules] = useState<V2Schedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(
    null
  );
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [wbsPath, setWbsPath] = useState<PathEntry[]>([]);

  // Committed (debounced) search term. The search box keeps raw keystrokes in
  // its own local state and only pushes the debounced value up here, so typing
  // never re-renders the heavy table.
  const [search, setSearch] = useState("");
  const [excludeCompleted, setExcludeCompleted] = useState(false);
  const [criticalOnly, setCriticalOnly] = useState(false);

  // Paginated, server-searched row list (infinite scroll).
  const [rows, setRows] = useState<ScheduleTableRow[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [totalActivities, setTotalActivities] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Derived: a null cursor means the (filtered) tree is exhausted.
  const hasMore = nextCursor !== null;

  // Bumped on every scope/sort/search change so stale in-flight responses can
  // detect they've been superseded and bail.
  const sessionRef = useRef(0);
  // Synchronous in-flight guard for loadMore — prevents a same-tick double
  // fetch before the loadingMore state has a chance to commit.
  const loadingRef = useRef(false);

  // One fetch for both the page-1 reset and infinite-scroll appends; `append`
  // selects replace-vs-append and whether to refresh the total.
  const runFetch = useCallback(
    (cursor: number | null, append: boolean, session: number) => {
      const { sortBy, sortDir } = sortParams(sorting);
      return fetchScheduleTable(selectedScheduleId as number, {
        wbsNodeId: selectedNodeId,
        cursor,
        limit: PAGE_LIMIT,
        q: search || null,
        sortBy,
        sortDir,
        excludeCompleted,
        criticalOnly,
      })
        .then((res) => {
          if (sessionRef.current !== session) return;
          setRows((prev) =>
            append ? [...prev, ...(res?.rows ?? [])] : res?.rows ?? []
          );
          setNextCursor(res?.next_cursor ?? null);
          if (!append) setTotalActivities(res?.total_activities ?? 0);
        })
        .catch(() => {
          if (sessionRef.current !== session) return;
          setNextCursor(null);
          if (!append) setRows([]);
        });
    },
    [selectedScheduleId, selectedNodeId, sorting, search, excludeCompleted, criticalOnly]
  );

  // Load schedule list on mount, auto-select first.
  useEffect(() => {
    fetchV2Schedules().then((scheds) => {
      setSchedules(scheds);
      if (scheds.length > 0) setSelectedScheduleId(scheds[0].id);
    });
  }, []);

  // Reset + fetch page 1 whenever scope, sort, or search changes.
  useEffect(() => {
    if (!selectedScheduleId) return;
    const session = ++sessionRef.current;
    setRows([]);
    setNextCursor(null);
    setLoading(true);
    runFetch(null, false, session).finally(() => {
      if (sessionRef.current === session) setLoading(false);
    });
  }, [selectedScheduleId, runFetch]);

  const loadMore = useCallback(() => {
    if (!selectedScheduleId || nextCursor === null || loadingRef.current || loading) {
      return;
    }
    loadingRef.current = true;
    const session = sessionRef.current;
    setLoadingMore(true);
    runFetch(nextCursor, true, session).finally(() => {
      loadingRef.current = false;
      if (sessionRef.current === session) setLoadingMore(false);
    });
  }, [selectedScheduleId, nextCursor, loading, runFetch]);

  const handleScheduleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = parseInt(e.target.value, 10);
    setSelectedScheduleId(id);
    setSelectedNodeId(null);
    setSorting([]);
    setWbsPath([]);
    setSearch("");
    setExcludeCompleted(false);
    setCriticalOnly(false);
  };

  const handleBreadcrumbClick = (index: number) => {
    const truncated = wbsPath.slice(0, index + 1);
    setWbsPath(truncated);
    setSelectedNodeId(truncated[truncated.length - 1].id);
  };

  const countLabel = search.trim()
    ? `${totalActivities} matching`
    : `${totalActivities} activities`;

  const scopeKey = buildScopeKey({
    scheduleId: selectedScheduleId,
    nodeId: selectedNodeId,
    sorting,
    search,
    excludeCompleted,
    criticalOnly,
  });

  return (
    <div className="schedule-page">
      <div className="schedule-page-header">
        {schedules.length > 1 && (
          <select
            className="schedule-select"
            value={selectedScheduleId ?? ""}
            onChange={handleScheduleChange}
          >
            {schedules.map((s) => (
              <option key={s.id} value={s.id}>
                {s.project_name || s.name}
              </option>
            ))}
          </select>
        )}
        {schedules.length === 1 && (
          <button
            className={`schedule-name ${wbsPath.length > 0 ? "clickable" : ""}`}
            onClick={() => { setWbsPath([]); setSelectedNodeId(null); }}
          >
            {schedules[0].project_name || schedules[0].name}
          </button>
        )}
        {wbsPath.length > 0 && (
          <nav className="header-breadcrumb">
            {wbsPath.map((entry, i) => (
              <React.Fragment key={entry.id}>
                <span className="header-breadcrumb-sep">&rsaquo;</span>
                <button
                  className={`header-breadcrumb-item ${i === wbsPath.length - 1 ? "current" : ""}`}
                  onClick={() => handleBreadcrumbClick(i)}
                >
                  {entry.name}
                </button>
              </React.Fragment>
            ))}
          </nav>
        )}
        <span className="schedule-page-stats">{countLabel}</span>
      </div>
      <div className="schedule-page-body">
        {selectedScheduleId && (
          <WBSSidebar
            scheduleId={selectedScheduleId}
            selectedNodeId={selectedNodeId}
            onSelectNode={setSelectedNodeId}
            path={wbsPath}
            onPathChange={setWbsPath}
          />
        )}
        <ScheduleView
          rows={rows}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchResetKey={selectedScheduleId}
          excludeCompleted={excludeCompleted}
          onExcludeCompletedChange={setExcludeCompleted}
          criticalOnly={criticalOnly}
          onCriticalOnlyChange={setCriticalOnly}
          scopeKey={scopeKey}
        />
      </div>
    </div>
  );
};

export default SchedulePage;
